import React from "react";
import { useQuery } from "react-query";
import {
  Box, Typography, Paper, CircularProgress, Tooltip, Zoom, Divider, Avatar, IconButton, Stack, Chip
} from "@mui/material";
import { ChevronLeft, ChevronRight, Today, AccessTime, Groups } from "@mui/icons-material";
import moment from "moment";
import { QK } from "../../base/qk";
import { getEmployeesDayTimeline } from "../../api/services/TimeLine/timeLine";

const DayTimelineCalendar = ({ selectedDate, setSelectedDate }) => {
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const handleDateChange = (amount) => {
    const newDate = moment(selectedDate).add(amount, "days").format("YYYY-MM-DD");
    setSelectedDate(newDate);
  };

  const { data: timelineResponse, isLoading, isFetching } = useQuery(
    [QK.dayTimelineData, selectedDate],
    () => getEmployeesDayTimeline(selectedDate),
    { 
      enabled: !!selectedDate, 
      refetchOnWindowFocus: false,
      keepPreviousData: true // এটি ব্লিঙ্কিং বন্ধ করার আসল সমাধান
    }
  );

  const displayData = timelineResponse?.data || [];

  // শুধুমাত্র একদম প্রথমবার যখন কোনো ডেটা নেই, তখন মেইন লোডার আসবে
  if (isLoading && !timelineResponse)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: 'center', height: '400px' }}>
        <CircularProgress size={30} thickness={4} />
      </Box>
    );

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Compact Date Picker */}
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton size="small" onClick={() => handleDateChange(-1)} sx={{ border: '1px solid #e2e8f0' }}>
          <ChevronLeft fontSize="small" />
        </IconButton>
        <Typography sx={{ fontWeight: 800, color: '#1a202c', fontSize: '14px', minWidth: '180px', textAlign: 'center' }}>
          {moment(selectedDate).format("ddd, Do MMM YYYY")}
        </Typography>
        <IconButton size="small" onClick={() => handleDateChange(1)} sx={{ border: '1px solid #e2e8f0' }}>
          <ChevronRight fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => setSelectedDate(moment().format("YYYY-MM-DD"))} sx={{ bgcolor: '#f7fafc' }}>
          <Today fontSize="small" />
        </IconButton>
        
        {/* রিফেচিং ইন্ডিকেটর (ব্লিঙ্ক না করে ছোট করে দেখাবে) */}
        {isFetching && <CircularProgress size={12} sx={{ ml: 1 }} />}
      </Stack>

      <Paper 
        elevation={0} 
        sx={{ 
          border: "1px solid #e2e8f0", 
          borderRadius: "12px", 
          overflow: "hidden", 
          bgcolor: '#fff',
          opacity: isFetching ? 0.7 : 1, // ফেচ করার সময় হালকা আবছা হবে, কিন্তু UI গায়েব হবে না
          transition: 'opacity 0.2s ease-in-out'
        }}
      >
        <Box sx={{ overflowX: "auto" }}>
          <Box sx={{ minWidth: 1200, position: "relative" }}>
            
            {/* Header */}
            <Box sx={{ display: "flex", bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", position: 'sticky', top: 0, zIndex: 1100 }}>
              <Box sx={{ 
                width: 180, p: 1.5, fontWeight: 800, borderRight: "1px solid #e2e8f0", 
                position: 'sticky', left: 0, bgcolor: '#f8fafc', zIndex: 1110, fontSize: '11px', color: '#475569' 
              }}>
                STAFF MEMBERS
              </Box>
              {hours.map((h) => (
                <Box key={h} sx={{ flex: 1, p: 1, textAlign: "center", fontSize: "10px", fontWeight: 700, color: "#94a3b8", borderRight: "1px solid #f1f5f9" }}>
                  {h.toString().padStart(2, "0")}:00
                </Box>
              ))}
            </Box>

            {/* Rows */}
            {displayData.map((item, idx) => (
              <Box key={idx} sx={{ display: "flex", borderBottom: "1px solid #f1f5f9", height: 65, position: "relative", '&:hover': { bgcolor: '#f8fafc' } }}>
                <Box sx={{ width: 180, p: 1, borderRight: "1px solid #e2e8f0", position: "sticky", left: 0, bgcolor: "#fff", zIndex: 10, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 30, height: 30, fontSize: '11px', bgcolor: '#3182ce', fontWeight: 700, color: '#fff' }}>
                    {item.resource_name?.split(" ").map(n => n[0]).join("")}
                  </Avatar>
                  <Box sx={{ overflow: 'hidden' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '12px', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.resource_name}
                    </Typography>
                    <Typography sx={{ fontSize: '9px', color: '#64748b', fontWeight: 600 }}>
                      {item.tasks?.length || 0} Assignments
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ flex: 1, position: "relative", display: "flex" }}>
                  {hours.map((h) => <Box key={h} sx={{ flex: 1, borderRight: "1px solid #f1f5f9" }} />)}

                  {item.tasks?.map((task, tIdx) => {
                    const start = moment(task.start_time, "HH:mm:ss");
                    const end = task.end_time ? moment(task.end_time, "HH:mm:ss") : start.clone().add(task.duration_minutes, 'minutes');
                    
                    const left = ((start.hours() + start.minutes() / 60) / 24) * 100;
                    const duration = moment.duration(end.diff(start)).asMinutes();
                    const width = (duration / (24 * 60)) * 100;
                    const isAlert = (task.employee_assigned || 0) < (task.carers_required || 1);

                    return (
                      <Tooltip
                        key={tIdx}
                        TransitionComponent={Zoom}
                        arrow
                        PopperProps={{
                          sx: {
                            "& .MuiTooltip-tooltip": {
                              bgcolor: "#ffffff",
                              color: "#1e293b",
                              boxShadow: "0px 10px 25px rgba(0,0,0,0.2)",
                              border: "1px solid #e2e8f0",
                              p: 0,
                              borderRadius: "10px",
                              maxWidth: "280px"
                            }
                          }
                        }}
                        title={
                          <Box sx={{ p: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                              <Typography sx={{ fontWeight: 800, fontSize: '14px', color: '#0f172a' }}>{task.client_name}</Typography>
                              <Chip label={task.visit_name} size="small" sx={{ height: 20, fontSize: '10px', fontWeight: 700, bgcolor: '#f1f5f9', color: '#475569' }} />
                            </Stack>
                            
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                              <AccessTime sx={{ fontSize: '16px', color: '#3182ce' }} />
                              <Typography sx={{ fontSize: '13px', fontWeight: 800, color: '#334155' }}>
                                {start.format("HH:mm")} — {end.format("HH:mm")}
                              </Typography>
                            </Stack>

                            <Divider sx={{ mb: 1.5, opacity: 0.6 }} />
                            
                            <Stack spacing={1}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Staff Requirement:</Typography>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 800, color: isAlert ? '#e11d48' : '#0f172a' }}>
                                        {task.employee_assigned} / {task.carers_required}
                                    </Typography>
                                </Stack>
                            </Stack>
                          </Box>
                        }
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            left: `${left}%`,
                            width: `${width}%`,
                            top: "12px",
                            height: 40,
                            bgcolor: task.shift_details?.shift_color || "#4299e1",
                            borderRadius: "6px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            px: 1.5,
                            cursor: "pointer",
                            borderLeft: `4px solid ${isAlert ? "#e11d48" : "rgba(0,0,0,0.15)"}`,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                            zIndex: 100,
                            transition: 'all 0.2s ease',
                            '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 6px 12px rgba(0,0,0,0.15)', zIndex: 105 }
                          }}
                        >
                          <Typography sx={{ fontWeight: 1000, fontSize: "10.5px", color: "#fff", whiteSpace: "nowrap", overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {task.client_name}
                          </Typography>
                          <Typography sx={{ fontSize: "9px", color: "rgba(255,255,255,0.95)", fontWeight: 700 }}>
                            {start.format("HH:mm")} - {end.format("HH:mm")}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default DayTimelineCalendar;